import styled from '@emotion/styled';
import { LoadMoreProps } from 'components/LoadMore';
import dayjs from 'lib/dayjs';
import { useNoticeLanguage, useTypeLabel } from './NoticeModal';
import Empty from 'components/Empty';
import { LoadMoreV2 } from 'components/LoadMore/v2';
import { useMemo } from 'react';
import { buryNoticeItem } from 'services/etc/bury';

const Item = styled.div<{ read: string }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 36px;
  padding: 0 16px;
  cursor: pointer;
  > div {
    flex: 1 0 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-weight: ${({ read }) => (read === 'true' ? 400 : 500)};
    color: ${({ read }) =>
      read === 'true' ? 'var(--fett-text-secondary)' : 'var(--fett-text-primary)'};
  }
  > span {
    flex: 0 0 auto;
    margin-left: 35px;
    font-size: 12px;
    color: var(--fett-text-quaternary);
  }
  &:hover {
    background: var(--fett-bg-hover);
    > div {
      color: var(--fett-app-primary-color);
    }
  }
`;

export interface NoticeListProps extends Omit<LoadMoreProps, 'children'> {
  list: Notice.Item[];
  onNoticeSelect: (notice: Notice.Item) => void;
}

interface NoticeListItemProps {
  data: Notice.Item;
  onNoticeSelect: (notice: Notice.Item) => void;
}

function NoticeListItem(props: NoticeListItemProps) {
  const {
    data: { id, type, isRead, createTimeStamp, caseId, noticeMap },
    onNoticeSelect,
  } = props;
  const { noticeLanguage } = useNoticeLanguage();

  const { t, getTypeLabel } = useTypeLabel();

  let title: string;
  if (type === '问题反馈') {
    title = t('notice_feedback_title', { caseId });
  } else {
    title = noticeMap?.[noticeLanguage]?.title ?? '';
  }

  return (
    <Item
      key={id}
      read={String(isRead)}
      onClick={() => {
        onNoticeSelect(props.data);
        buryNoticeItem({
          id,
          title,
          type,
          caseId,
        });
      }}
    >
      <div>{`[${getTypeLabel(type)}] ${title}`}</div>
      <span>{dayjs(createTimeStamp).format('YYYY-MM-DD HH:mm:ss')}</span>
    </Item>
  );
}

function NoticeList(props: NoticeListProps) {
  const { list, onNoticeSelect, hasMore, onLoadMore } = props;

  const { systemLanguage } = useNoticeLanguage();

  const renderList = useMemo(
    () =>
      list.filter(({ languages = [] }) => {
        return languages.length === 0 || languages.includes(systemLanguage);
      }),
    [list, systemLanguage],
  );

  if (!hasMore && renderList.length === 0) {
    return <Empty />;
  }

  return (
    <LoadMoreV2
      data={renderList}
      itemContent={(_, notice) => <NoticeListItem data={notice} onNoticeSelect={onNoticeSelect} />}
      hasMore={hasMore}
      onLoadMore={onLoadMore}
      onLoad={onLoadMore}
    />
  );
}

export default NoticeList;
